import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LocalizedLink from '../../i18n/LocalizedLink';
import { getLocaleFromPathname, stripLocalePrefix, addLocalePrefix, type Locale } from '../../i18n/routing';
import { ThemeToggle } from '../../theme/ThemeToggle';

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = React.useState(false);

  const normalizedPathname = stripLocalePrefix(location.pathname);
  const rawLocale = getLocaleFromPathname(location.pathname);
  const localeOrder: Locale[] = ['es', 'en', 'pt'];
  const normalizedLocale = localeOrder.includes(rawLocale) ? rawLocale : 'es';

  const getSwitchLabel = (targetLocale: Locale) => {
    if (targetLocale === 'en') return t('common.switchToEn');
    if (targetLocale === 'pt') return t('common.switchToPt');
    return t('common.switchToEs');
  };

  const getLocaleFlag = (targetLocale: Locale) => {
    if (targetLocale === 'en') return '🇺🇸';
    if (targetLocale === 'pt') return '🇵🇹';
    return '🇪🇸';
  };

  const navigateToLocale = (targetLocale: Locale) => {
    const base = stripLocalePrefix(location.pathname);
    const targetPath = addLocalePrefix(base, targetLocale) + location.search + location.hash;
    navigate(targetPath);
    setLanguageMenuOpen(false);
  };
  
  // No mostrar header en rutas de juego
  const hideHeaderRoutes = ['/create', '/join', '/game', '/table', '/room'];
  const shouldHide = hideHeaderRoutes.some(route => normalizedPathname.startsWith(route));

  React.useEffect(() => {
    setMobileMenuOpen(false);
    setLanguageMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);
  
  if (shouldHide) {
    return null;
  }

  return (
    <header className="sticky top-0 z-50 bg-blue-50/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-blue-200 dark:border-gray-800 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <LocalizedLink 
            to="/" 
            className="flex items-center min-w-0 space-x-2 text-xl font-bold text-indigo-700 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200 transition-colors"
            aria-label={t('common.backHomeAria')}
          >
            <span className="text-2xl">🎭</span>
            <span className="truncate">Impostor Game</span>
          </LocalizedLink>

          {/* Desktop navigation */}
          <nav className="hidden md:flex items-center space-x-2 sm:space-x-4">
            <LocalizedLink 
              to="/" 
              className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all"
            >
              {t('nav.home')}
            </LocalizedLink>
            <LocalizedLink 
              to="/blog" 
              className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all"
            >
              {t('nav.blog')}
            </LocalizedLink>
            <LocalizedLink 
              to="/contact" 
              className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all"
            >
              {t('nav.contact')}
            </LocalizedLink>
            <LocalizedLink 
              to="/create" 
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md hover:shadow-lg transition-all"
            >
              {t('nav.play')}
            </LocalizedLink>

            <ThemeToggle className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all" />

            <div className="relative">
              <button
                type="button"
                onClick={() => setLanguageMenuOpen((v) => !v)}
                className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all"
                aria-label={t('common.toggleMenu')}
                aria-expanded={languageMenuOpen}
              >
                <span aria-hidden="true" className="text-lg leading-none">
                  {getLocaleFlag(normalizedLocale)}
                </span>
              </button>
              {languageMenuOpen && (
                <div className="absolute right-0 mt-2 w-44 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg z-50">
                  {(['es', 'en', 'pt'] as Locale[]).map((lng) => (
                    <button
                      key={lng}
                      type="button"
                      onClick={() => navigateToLocale(lng)}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-gray-800 transition-colors text-left ${
                        normalizedLocale === lng ? 'font-semibold text-indigo-700 dark:text-indigo-200' : 'text-gray-700 dark:text-gray-200'
                      }`}
                    >
                      <span aria-hidden="true">{getLocaleFlag(lng)}</span>
                      <span>{getSwitchLabel(lng)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <div className="flex md:hidden items-center">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(v => !v)}
              className="inline-flex items-center justify-center p-2 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all"
              aria-label={mobileMenuOpen ? t('common.close') : t('common.menu')}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-nav"
            >
              <svg
                className="h-6 w-6"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                {mobileMenuOpen ? (
                  <path d="M18 6L6 18M6 6l12 12" />
                ) : (
                  <path d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen ? (
          <div id="mobile-nav" className="md:hidden pb-4">
            <div className="pt-2 space-y-1">
              <LocalizedLink
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all"
              >
                {t('nav.home')}
              </LocalizedLink>
              <LocalizedLink
                to="/blog"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all"
              >
                {t('nav.blog')}
              </LocalizedLink>
              <LocalizedLink
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all"
              >
                {t('nav.contact')}
              </LocalizedLink>
              <LocalizedLink
                to="/create"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-4 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md hover:shadow-lg transition-all"
              >
                {t('nav.play')}
              </LocalizedLink>

              <div className="flex items-center justify-between gap-2 px-1 pt-2">
                <ThemeToggle className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all" />
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setLanguageMenuOpen((v) => !v)}
                    className="px-3 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-gray-800 hover:text-indigo-700 dark:hover:text-indigo-200 transition-all"
                    aria-label={t('common.toggleMenu')}
                    aria-expanded={languageMenuOpen}
                  >
                    <span aria-hidden="true" className="text-lg leading-none">
                      {getLocaleFlag(normalizedLocale)}
                    </span>
                  </button>
                  {languageMenuOpen && (
                    <div className="absolute right-0 mt-2 w-44 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg z-50">
                      {(['es', 'en', 'pt'] as Locale[]).map((lng) => (
                        <button
                          key={lng}
                          type="button"
                          onClick={() => navigateToLocale(lng)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-gray-800 transition-colors text-left ${
                            normalizedLocale === lng ? 'font-semibold text-indigo-700 dark:text-indigo-200' : 'text-gray-700 dark:text-gray-200'
                          }`}
                        >
                          <span aria-hidden="true">{getLocaleFlag(lng)}</span>
                          <span>{getSwitchLabel(lng)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default Header;
